/*
Copyright (C) 2017  Cloudbase Solutions SRL
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import * as React from "react";
import { observer } from "mobx-react";
import styled, { css } from "styled-components";

import { ThemePalette, ThemeProps } from "@src/components/Theme";

const Row = styled.div<any>`
  display: flex;
  justify-content: center;
`;
// An Ops segmented control: one bordered group, the selected segment in the brand fill.
const Wrapper = styled.div<any>`
  display: inline-flex;
  align-self: center;
  margin: 0 auto;
  padding: 2px;
  gap: 2px;
  background: ${ThemePalette.grayscale[7]};
  border: 1px solid ${ThemePalette.grayscale[0]};
  border-radius: 8px;
`;
const Item = styled.div<any>`
  min-width: 96px;
  height: 28px;
  padding: 0 14px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  background: ${(props: any) => (props.selected ? ThemePalette.primary : "transparent")};
  color: ${(props: any) => (props.selected ? "white" : ThemePalette.grayscale[5])};
  font-size: 13px;
  font-weight: ${(props: any) => (props.selected ? 600 : 500)};
  white-space: nowrap;
  transition: all ${ThemeProps.animations.swift};
  cursor: pointer;
  outline: none;
  &:hover {
    ${(props: any) => (!props.selected ? css`color: ${ThemePalette.black}; background: var(--t9-bg-surface);` : "")}
  }
  &:focus-visible {
    box-shadow: 0 0 0 2px ${ThemePalette.accent};
  }
`;

type ItemType = { value: string; label: string };
type Props = {
  items: Array<ItemType>;
  selectedValue?: string;
  onChange?: (item: ItemType) => void;
  className?: string;
  style?: React.CSSProperties;
};
@observer
class ToggleButtonBar extends React.Component<Props> {
  change(item: ItemType) {
    if (this.props.onChange) {
      this.props.onChange(item);
    }
  }

  handleKeyPress(e: React.KeyboardEvent<HTMLDivElement>, item: ItemType) {
    if (e.key !== " ") {
      return;
    }
    e.preventDefault();
    this.change(item);
  }

  render() {
    if (!this.props.items) {
      return null;
    }

    return (
      <Row className={this.props.className} style={this.props.style}>
        <Wrapper role="tablist">
        {this.props.items.map(item => (
          <Item
            key={item.value}
            selected={this.props.selectedValue === item.value}
            role="tab"
            aria-selected={this.props.selectedValue === item.value}
            onClick={() => {
              this.change(item);
            }}
            tabIndex={0}
            onKeyPress={(e: React.KeyboardEvent<HTMLDivElement>) => {
              this.handleKeyPress(e, item);
            }}
          >
            {item.label}
          </Item>
        ))}
        </Wrapper>
      </Row>
    );
  }
}

export default ToggleButtonBar;
